// Khung chung các màn Tài liệu: Layout CMS + tiêu đề + thao tác + theme màu thương hiệu
import React, { useEffect } from 'react';
import { ConfigProvider, Modal, Select, Tooltip, message } from 'antd';
import { FlaskConical } from 'lucide-react';
import Layout from '../../../Layout';
import useDocsStore, { type DocRole } from '../store';
import { clearFiles, deleteFiles } from '../fileStore';
import { BRAND, ROLE_LABEL, useDocAuth } from '../utils';

export const docTheme = {
  token: { colorPrimary: BRAND.button, colorLink: BRAND.button, borderRadius: 8, fontFamily: 'inherit' },
};

/** Giả lập vai trò — chỉ hiện ở môi trường dev để thử luồng nhân viên / người duyệt / admin */
export function DemoRoleSwitch() {
  const demoRole = useDocsStore(s => s.demoRole);
  const setDemoRole = useDocsStore(s => s.setDemoRole);
  const resetDemo = useDocsStore(s => s.resetDemo);
  const { realRole } = useDocAuth();
  const [modal, ctx] = Modal.useModal();
  if (process.env.NODE_ENV !== 'development') return null;
  const onChange = (v: DocRole | 'real' | 'reset') => {
    if (v !== 'reset') return setDemoRole(v === 'real' ? null : v);
    modal.confirm({
      title: 'Khôi phục dữ liệu mẫu?',
      content: 'Toàn bộ loại, danh mục, tài liệu và file đã tải lên trên trình duyệt này sẽ bị xóa và thay bằng dữ liệu mẫu ban đầu.',
      okText: 'Khôi phục', okButtonProps: { danger: true }, cancelText: 'Hủy',
      onOk: async () => { await clearFiles(); resetDemo(); message.success('Đã khôi phục dữ liệu mẫu'); },
    });
  };
  return (
    <div className="flex items-center gap-1.5 rounded-lg border border-dashed border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-700">
      <Tooltip title="Chỉ có ở môi trường dev: giả lập vai trò để thử quyền duyệt, quản lý, cấu hình">
        <FlaskConical className="h-3.5 w-3.5" />
      </Tooltip>
      <Select<DocRole | 'real' | 'reset'>
        size="small"
        variant="borderless"
        value={demoRole || 'real'}
        onChange={onChange}
        popupMatchSelectWidth={false}
        options={[
          { value: 'real', label: `Vai trò thật (${ROLE_LABEL[realRole]})` },
          { value: 'staff', label: 'Giả lập: Nhân viên' },
          { value: 'manager', label: 'Giả lập: Quản lý tài liệu' },
          { value: 'admin', label: 'Giả lập: Admin' },
          { value: 'reset', label: <span className="text-red-500">↺ Khôi phục dữ liệu mẫu</span> },
        ]}
      />
      {ctx}
    </div>
  );
}

interface Props {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  breadcrumb?: React.ReactNode;
  children: React.ReactNode;
}

export default function DocShell({ title, subtitle, actions, breadcrumb, children }: Props) {
  const purgeExpiredTrash = useDocsStore(s => s.purgeExpiredTrash);
  useEffect(() => {
    // Thùng rác tự dọn tài liệu quá 30 ngày
    const fileIds = purgeExpiredTrash();
    if (fileIds.length) deleteFiles(fileIds);
  }, [purgeExpiredTrash]);

  return (
    <ConfigProvider theme={docTheme}>
      <Layout>
        {breadcrumb}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[18px] font-bold text-000000e0">{title}</h1>
            {subtitle && <div className="mt-0.5 text-sm text-slate-500">{subtitle}</div>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <DemoRoleSwitch />
            {actions}
          </div>
        </div>
        {children}
      </Layout>
    </ConfigProvider>
  );
}

/** Màn báo không có quyền / không tìm thấy, dùng trong các màn chi tiết */
export function EmptyState({ icon, title, desc, action }: { icon: React.ReactNode; title: string; desc?: string; action?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center">
      <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-400">{icon}</div>
      <div className="text-base font-semibold text-slate-700">{title}</div>
      {desc && <div className="mt-1 max-w-md text-sm text-slate-500">{desc}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
